// @ts-nocheck
/**
 * Announcement bar — infinite track-based slider.
 *
 * DOM layout after #insertClones():
 *   [clone-of-last]  [slide-0]  [slide-1]  …  [slide-n-1]  [clone-of-first]
 *        pos 0         pos 1      pos 2             pos n         pos n+1
 *
 * When we animate to a clone we immediately teleport (no animation) back to
 * the matching real slide — the user only sees one smooth motion.
 */
export class AnnouncementBar extends HTMLElement {
  // Real slide index (0 … n-1), always in sync even mid-clone-jump
  #current     = 0;

  // Pending jump back from clone to real counterpart
  #jumpTimeout = undefined;
  #isJumping   = false;

  #interval  = undefined;
  #resizeObs = null;

  // Mouse drag
  #dragStartX      = 0;
  #dragStartTrackX = 0;
  #isDragging      = false;
  #wasDrag         = false;

  // Touch drag
  #touchStartX      = 0;
  #touchStartTrackX = 0;

  static #EASE     = 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  static #DURATION = 570; // ms — fires just after the 550 ms transition

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    if (!this.slides.length) return;
    requestAnimationFrame(() => {
      if (this.slides.length > 1) this.#insertClones();
      this.#moveTo(0, false);
      this.#bindEvents();
      this.#startAutoplay();
    });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#resizeObs?.disconnect();
    clearTimeout(this.#jumpTimeout);
    document.removeEventListener('mousemove', this.#onMouseMove);
    document.removeEventListener('mouseup',   this.#onMouseUp);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.removeEventListener('click', this.#onClick, true);
  }

  // ─── Public ────────────────────────────────────────────────────────────────

  next()     { this.#go(1); }
  previous() { this.#go(-1); }

  // ─── DOM refs ──────────────────────────────────────────────────────────────

  /** Real slides only — clones carry [data-clone] and are excluded */
  get slides() {
    return Array.from(this.querySelectorAll('[ref="slides[]"]:not([data-clone])'));
  }

  get #track() { return this.querySelector('.announcement-bar__track'); }

  /** One slide width = the container's own width */
  get #w() { return this.offsetWidth; }

  get #hasClones() { return this.slides.length > 1; }

  get #autoplayMs() {
    const v = parseInt(this.getAttribute('autoplay') || '', 10);
    return Number.isNaN(v) ? 0 : v * 1000;
  }

  /** Track's current translateX in px, safe to call mid-animation */
  #liveX() {
    const t = this.#track;
    return t ? new DOMMatrix(getComputedStyle(t).transform).m41 : 0;
  }

  /** DOM position for a real slide index (shifted by 1 when clones exist) */
  #domPos(realIndex) {
    return this.#hasClones ? realIndex + 1 : realIndex;
  }

  // ─── Clone setup ───────────────────────────────────────────────────────────

  #insertClones() {
    const track  = this.#track;
    const slides = this.slides;
    if (!track || slides.length < 2) return;

    const clone = (el, key) => {
      const c = el.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-clone', key);
      return c;
    };

    track.prepend(clone(slides[slides.length - 1], 'last')); // pos 0
    track.append(clone(slides[0], 'first'));                  // pos n+1
  }

  // ─── Navigation ────────────────────────────────────────────────────────────

  /** Go to a real slide index, with optional animation */
  #moveTo(realIndex, animate = true) {
    const track = this.#track;
    const w     = this.#w;
    if (!track || !w) return;

    this.#current = realIndex;
    track.style.transition = animate ? AnnouncementBar.#EASE : 'none';
    track.style.transform  = `translateX(${-this.#domPos(realIndex) * w}px)`;

    this.slides.forEach((s, i) =>
      s.setAttribute('aria-hidden', String(i !== realIndex))
    );
  }

  /** Advance by delta (±1). Uses clone trick for seamless infinite wrap. */
  #go(delta) {
    if (this.#isJumping) return;

    const n     = this.slides.length;
    const track = this.#track;
    const w     = this.#w;
    if (!track || !w) return;

    // Target real index (wraps correctly)
    const realNext  = ((this.#current + delta) % n + n) % n;

    // DOM position to animate to (may land on a clone)
    const domTarget = this.#hasClones
      ? this.#current + 1 + delta  // adjacent DOM slot (clone at 0 or n+1)
      : realNext;

    track.style.transition = AnnouncementBar.#EASE;
    track.style.transform  = `translateX(${-domTarget * w}px)`;

    this.#current = realNext;
    this.slides.forEach((s, i) => s.setAttribute('aria-hidden', String(i !== realNext)));

    // If we landed on a clone, schedule the invisible teleport to the real slide
    const hitClone = this.#hasClones && (domTarget === 0 || domTarget === n + 1);
    if (hitClone) {
      this.#isJumping  = true;
      this.#jumpTimeout = setTimeout(() => {
        track.style.transition = 'none';
        track.style.transform  = `translateX(${-this.#domPos(this.#current) * w}px)`;
        this.#isJumping  = false;
        this.#jumpTimeout = undefined;
      }, AnnouncementBar.#DURATION);
    }
  }

  /** Cancel a pending clone-jump and land on the real slide immediately */
  #cancelJump() {
    if (!this.#isJumping) return;
    clearTimeout(this.#jumpTimeout);
    this.#jumpTimeout = undefined;
    this.#isJumping   = false;
    const track = this.#track;
    const w     = this.#w;
    if (track && w) {
      track.style.transition = 'none';
      track.style.transform  = `translateX(${-this.#domPos(this.#current) * w}px)`;
    }
  }

  /** Decide which slide to snap to after a drag ends */
  #snapAfterDrag(dx) {
    if (Math.abs(dx) >= this.#w * 0.15) {
      dx < 0 ? this.next() : this.previous();
    } else {
      this.#moveTo(this.#current); // rubber-band back
    }
  }

  // ─── Autoplay ──────────────────────────────────────────────────────────────

  #startAutoplay() {
    if (!this.#autoplayMs) return;
    this.#stopAutoplay();
    this.#interval = setInterval(() => {
      if (!document.hidden && !this.#isDragging) this.next();
    }, this.#autoplayMs);
  }

  #stopAutoplay() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  // ─── Event binding ─────────────────────────────────────────────────────────

  #bindEvents() {
    this.addEventListener('mouseenter', () => this.#stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (!this.#isDragging) this.#startAutoplay();
    });

    this.addEventListener('mousedown', this.#onMouseDown);
    document.addEventListener('mousemove', this.#onMouseMove);
    document.addEventListener('mouseup',   this.#onMouseUp);

    this.addEventListener('dragstart', (e) => e.preventDefault());
    this.addEventListener('click', this.#onClick, true);

    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchmove',  this.#onTouchMove,  { passive: true });
    this.addEventListener('touchend',   this.#onTouchEnd,   { passive: true });

    // Re-snap on resize / orientation change
    this.#resizeObs = new ResizeObserver(() => {
      clearTimeout(this.#jumpTimeout);
      this.#isJumping = false;
      this.#moveTo(this.#current, false);
    });
    this.#resizeObs.observe(this);

    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  // ─── Mouse drag ────────────────────────────────────────────────────────────

  #onMouseDown = (e) => {
    e.preventDefault();
    this.#cancelJump(); // if mid-jump, land on real slide before drag starts
    const track = this.#track;
    if (track) {
      track.style.transition = 'none';
      track.style.transform  = `translateX(${this.#liveX()}px)`;
    }
    this.#dragStartX      = e.clientX;
    this.#dragStartTrackX = this.#liveX();
    this.#isDragging      = true;
    this.#wasDrag         = false;
    this.style.cursor     = 'grabbing';
    this.style.userSelect = 'none';
  };

  #onMouseMove = (e) => {
    if (!this.#isDragging) return;
    const dx = e.clientX - this.#dragStartX;
    if (Math.abs(dx) > 8) this.#wasDrag = true;
    const track = this.#track;
    if (track) track.style.transform = `translateX(${this.#dragStartTrackX + dx}px)`;
  };

  #onMouseUp = (e) => {
    if (!this.#isDragging) return;
    this.#isDragging      = false;
    this.style.cursor     = '';
    this.style.userSelect = '';
    this.#snapAfterDrag(e.clientX - this.#dragStartX);
    if (!this.matches(':hover')) this.#startAutoplay();
  };

  #onClick = (e) => {
    if (this.#wasDrag) {
      e.preventDefault();
      e.stopPropagation();
      this.#wasDrag = false;
    }
  };

  // ─── Touch drag ────────────────────────────────────────────────────────────

  #onTouchStart = (e) => {
    this.#cancelJump();
    const track = this.#track;
    if (track) {
      track.style.transition = 'none';
      track.style.transform  = `translateX(${this.#liveX()}px)`;
    }
    this.#touchStartX      = e.changedTouches[0].clientX;
    this.#touchStartTrackX = this.#liveX();
    this.#isDragging       = true;
    this.#stopAutoplay();
  };

  #onTouchMove = (e) => {
    if (!this.#isDragging) return;
    const dx    = e.changedTouches[0].clientX - this.#touchStartX;
    const track = this.#track;
    if (track) track.style.transform = `translateX(${this.#touchStartTrackX + dx}px)`;
  };

  #onTouchEnd = (e) => {
    this.#isDragging = false;
    this.#snapAfterDrag(e.changedTouches[0].clientX - this.#touchStartX);
    this.#startAutoplay();
  };

  // ─── Visibility ────────────────────────────────────────────────────────────

  #onVisibilityChange = () => {
    document.hidden ? this.#stopAutoplay() : this.#startAutoplay();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
